// Verwaltung der Intercompany-/Verbund-Beziehungen (Liste + Anlage/Bearbeitung).
// Ergebniswirksame (Miete, Management Fee) und Finanzierungs-Beziehungen
// (Gewinnausschuettung, Einlage, Gesellschafterdarlehen).

import { useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Checkbox,
  EmptyState,
  Input,
  LoadingState,
  Modal,
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import {
  AUFWAND_POSITIONS,
  ERTRAG_POSITIONS,
  EUR,
  IC_KINDS,
  IC_KIND_LABELS,
  MONTHS,
  effectForKind,
  type Company,
  type IcLinkRow,
} from './konzern-shared.ts'

export function IntercompanyModal({
  open, onOpenChange, year, companies, onChanged,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  year: number
  companies: Company[]
  onChanged: () => void
}) {
  const [links, setLinks] = useState<IcLinkRow[]>([])
  const [loading, setLoading] = useState(false)
  const [editing, setEditing] = useState<IcLinkRow | null>(null)
  const [showForm, setShowForm] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const res = await apiClient.get<IcLinkRow[]>(`/liquidity-planning/intercompany?year=${year}`)
      setLinks(Array.isArray(res.data) ? res.data : [])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { if (open) void load() /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [open])

  async function handleDelete(id: number) {
    if (!confirm('Beziehung wirklich löschen?')) return
    await apiClient.delete(`/liquidity-planning/intercompany/${id}`)
    await load()
    onChanged()
  }

  const companyName = (key: string) => companies.find((c) => c.key === key)?.name ?? key

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Intercompany / Verbund-Beziehungen"
      description={`Geschäftsjahr ${year} – z.B. „implec Weser mietet bei Karmoni VV". Wird in beide Pläne gespiegelt und im Konzern eliminiert.`}
      size="full"
    >
      <div className="space-y-3">
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => { setEditing(null); setShowForm(true) }}>
            <Plus className="size-4 mr-1" /> Beziehung
          </Button>
        </div>
        {loading ? (
          <LoadingState label="Lade Beziehungen …" />
        ) : links.length === 0 ? (
          <EmptyState title="Keine Verbund-Beziehungen" description="Lege z.B. die Miete zwischen zwei Gesellschaften an." />
        ) : (
          <table className="w-full text-small border-collapse">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="p-2">Bezeichnung</th>
                <th className="p-2">Art</th>
                <th className="p-2">Empfänger (Geld rein)</th>
                <th className="p-2">Zahler (Geld raus)</th>
                <th className="p-2 text-right">€/Monat</th>
                <th className="p-2">Zeitraum</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {links.map((l) => (
                <tr key={l.id} className="border-b border-gray-100">
                  <td className="p-2">{l.label}{!l.active && <span className="text-gray-400"> (inaktiv)</span>}</td>
                  <td className="p-2 text-gray-600">{IC_KIND_LABELS[l.kind] ?? l.kind}</td>
                  <td className="p-2">{companyName(l.providerKey)}</td>
                  <td className="p-2">{companyName(l.recipientKey)}</td>
                  <td className="p-2 text-right tabular-nums">{EUR.format(l.amountMonthly)}</td>
                  <td className="p-2 text-gray-600">{MONTHS[l.startMonth - 1]}–{l.endMonth ? MONTHS[l.endMonth - 1] : 'Dez'}</td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <Button variant="ghost" size="xs" onClick={() => { setEditing(l); setShowForm(true) }}>Bearbeiten</Button>
                    <Button variant="ghost" size="xs" onClick={() => handleDelete(l.id)}>Löschen</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <IcForm
        open={showForm}
        onOpenChange={setShowForm}
        year={year}
        companies={companies}
        entry={editing}
        onSaved={() => { setShowForm(false); void load(); onChanged() }}
      />
    </Modal>
  )
}

function IcForm({
  open, onOpenChange, year, companies, entry, onSaved,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  year: number
  companies: Company[]
  entry: IcLinkRow | null
  onSaved: () => void
}) {
  const [label, setLabel] = useState('')
  const [kind, setKind] = useState<string>('miete')
  const [providerKey, setProviderKey] = useState('')
  const [recipientKey, setRecipientKey] = useState('')
  const [providerPosition, setProviderPosition] = useState('sonstige_ertraege')
  const [recipientPosition, setRecipientPosition] = useState('raumkosten')
  const [amountMonthly, setAmountMonthly] = useState(0)
  const [startMonth, setStartMonth] = useState(1)
  const [endMonth, setEndMonth] = useState<number | null>(null)
  const [active, setActive] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (entry) {
      setLabel(entry.label); setKind(entry.kind)
      setProviderKey(entry.providerKey); setRecipientKey(entry.recipientKey)
      setProviderPosition(entry.providerPosition); setRecipientPosition(entry.recipientPosition)
      setAmountMonthly(entry.amountMonthly); setStartMonth(entry.startMonth)
      setEndMonth(entry.endMonth); setActive(entry.active)
    } else {
      setLabel(''); setKind('miete')
      setProviderKey(companies[0]?.key ?? ''); setRecipientKey(companies[1]?.key ?? companies[0]?.key ?? '')
      setProviderPosition('sonstige_ertraege'); setRecipientPosition('raumkosten')
      setAmountMonthly(0); setStartMonth(1); setEndMonth(null); setActive(true)
    }
    setError(null)
  }, [open, entry, companies])

  const effect = effectForKind(kind)

  async function handleSubmit() {
    setSubmitting(true)
    setError(null)
    try {
      const payload = {
        year, label, kind, effect, providerKey, recipientKey,
        providerPosition, recipientPosition,
        providerBsPosition: 'bs_ford_verbund', recipientBsPosition: 'bs_verb_verbund',
        amountMonthly, startMonth, endMonth, active,
      }
      if (entry) await apiClient.put(`/liquidity-planning/intercompany/${entry.id}`, payload)
      else await apiClient.post('/liquidity-planning/intercompany', payload)
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={entry ? 'Beziehung bearbeiten' : 'Neue Verbund-Beziehung'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Abbrechen</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting || !label || !providerKey || !recipientKey || providerKey === recipientKey || amountMonthly <= 0}>
            {submitting ? 'Speichere…' : entry ? 'Speichern' : 'Anlegen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <Input label="Bezeichnung" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="z.B. Miete implec Weser → Karmoni VV" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-small font-medium text-gray-700 mb-1 block">Art</label>
            <SelectRoot value={kind} onValueChange={setKind}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{IC_KINDS.map((k) => <SelectItem key={k} value={k}>{IC_KIND_LABELS[k]}</SelectItem>)}</SelectContent>
            </SelectRoot>
          </div>
          <Input label="Betrag €/Monat" type="number" step="50" value={amountMonthly} onChange={(e) => setAmountMonthly(Number(e.target.value))} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-small font-medium text-gray-700 mb-1 block">Empfänger (Geld rein{effect === 'pnl' ? ', Ertrag' : ''})</label>
            <SelectRoot value={providerKey} onValueChange={setProviderKey}>
              <SelectTrigger><SelectValue placeholder="Gesellschaft" /></SelectTrigger>
              <SelectContent>{companies.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
            </SelectRoot>
          </div>
          <div>
            <label className="text-small font-medium text-gray-700 mb-1 block">Zahler (Geld raus{effect === 'pnl' ? ', Aufwand' : ''})</label>
            <SelectRoot value={recipientKey} onValueChange={setRecipientKey}>
              <SelectTrigger><SelectValue placeholder="Gesellschaft" /></SelectTrigger>
              <SelectContent>{companies.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
            </SelectRoot>
          </div>
        </div>
        {effect === 'pnl' ? (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">Ertrags-Position (Empfänger)</label>
              <SelectRoot value={providerPosition} onValueChange={setProviderPosition}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ERTRAG_POSITIONS.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}</SelectContent>
              </SelectRoot>
            </div>
            <div>
              <label className="text-small font-medium text-gray-700 mb-1 block">Aufwands-Position (Zahler)</label>
              <SelectRoot value={recipientPosition} onValueChange={setRecipientPosition}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{AUFWAND_POSITIONS.map((p) => <SelectItem key={p.code} value={p.code}>{p.label}</SelectItem>)}</SelectContent>
              </SelectRoot>
            </div>
          </div>
        ) : (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-2 text-caption text-amber-800">
            <strong>Liquiditätswirksam, nicht ergebniswirksam.</strong>{' '}
            {kind === 'ausschuettung' && 'Geld fließt vom Zahler zum Empfänger; mindert das Eigenkapital (Gewinnrücklage) des Zahlers und erhöht es beim Empfänger. Berührt EBITDA/Gewinn nicht.'}
            {kind === 'einlage' && 'Geld fließt vom Zahler zum Empfänger; beim Empfänger Kapitalrücklage, beim Zahler Beteiligung (Anteile). Berührt EBITDA/Gewinn nicht.'}
            {kind === 'darlehen' && 'Geld fließt vom Zahler zum Empfänger; beim Empfänger Verbindlichkeit, beim Zahler Forderung ggü. Gesellschafter. Berührt EBITDA/Gewinn nicht.'}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Start-Monat" type="number" min={1} max={12} value={startMonth} onChange={(e) => setStartMonth(Number(e.target.value))} />
          <Input label="End-Monat (leer = Dez)" type="number" min={1} max={12} value={endMonth ?? ''} onChange={(e) => setEndMonth(e.target.value ? Number(e.target.value) : null)} />
        </div>
        <Checkbox label="Aktiv" checked={active} onChange={() => setActive((v) => !v)} />
        {providerKey === recipientKey && providerKey !== '' && (
          <p className="text-small text-amber-600">Empfänger und Zahler müssen unterschiedliche Gesellschaften sein.</p>
        )}
        {error && <p className="text-small text-red-600">{error}</p>}
      </div>
    </Modal>
  )
}
